import { ArrowUpRight, CheckCircle2, CircleAlert, Link2, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { KycStatusPill } from "@/modules/verification/components/KycStatusPill";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

import { useVerificationLink } from "../hooks/useVerificationLink";
import type { CardholderBoardItem, VerificationDiscoveryCategory } from "../types/cardholderOnboarding.types";

// Same alphabet coloca-nest accepts for an applicant id (`APPLICANT_ID_PATTERN`).
const VERIFICATION_ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;
const ACCOUNT_VERIFICATION_ROUTE = "/app/account-verification";

export interface LinkVerificationDialogProps {
  item: CardholderBoardItem;
  providerLabel: string;
  onClose: () => void;
  onLinked: () => void;
}

export function LinkVerificationDialog({
  item,
  providerLabel,
  onClose,
  onLinked,
}: LinkVerificationDialogProps) {
  const { t } = useTranslations("cardholderOnboarding");
  // The organization's own identity is linked from account verification, behind the 2FA flow built for it.
  const ownIdentity = item.is_organization_identity;
  const link = useVerificationLink(item.identity_id, t("error_generic"), !ownIdentity);
  const [manualId, setManualId] = useState("");
  const trimmedId = manualId.trim();
  const manualInvalid = trimmedId.length > 0 && !VERIFICATION_ID_PATTERN.test(trimmedId);
  const busy = link.phase === "searching" || link.phase === "linking";
  const name = item.name ?? t("unnamed");

  const handleLink = async () => {
    if (await link.link()) onLinked();
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title={t("link_title")}
      description={t("link_description", { name })}
      footer={
        link.phase === "linked" ? (
          <ColocaButton variant="primary" onClick={onClose}>
            {t("close")}
          </ColocaButton>
        ) : (
          <>
            <ColocaButton variant="ghost" disabled={busy} onClick={onClose}>
              {t("cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              icon={Link2}
              loading={link.phase === "linking"}
              disabled={link.phase !== "found"}
              onClick={() => void handleLink()}
            >
              {t("link_confirm")}
            </ColocaButton>
          </>
        )
      }
    >
      <div className="space-y-4">
        {link.phase === "searching" && (
          <div className="space-y-2" aria-busy>
            <p className="text-sm text-muted-foreground">{t("link_searching")}</p>
            <ColocaSkeleton className="h-14 w-full" />
          </div>
        )}

        {link.phase === "error" && link.errorMessage && (
          <ColocaAlert variant="destructive" icon={CircleAlert} text={link.errorMessage} />
        )}

        {(link.phase === "found" || link.phase === "linking") && link.result && (
          <div className="space-y-2 rounded-xl border border-border p-3">
            <p className="text-sm font-medium">{t("link_found")}</p>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {link.result.mapped_status && <KycStatusPill status={link.result.mapped_status} />}
              {link.result.match_source && (
                <span>{t("link_match_by", { field: t(`match_${link.result.match_source}`) })}</span>
              )}
            </div>
          </div>
        )}

        {link.phase === "other_identity" && (
          <ColocaAlert variant="warning" icon={CircleAlert} text={t("link_other_identity")} />
        )}

        {link.phase === "linked_elsewhere" && (
          <ColocaAlert variant="destructive" icon={CircleAlert} text={t("link_linked_elsewhere")} />
        )}

        {link.phase === "not_found" && !ownIdentity && (
          <ColocaAlert
            variant="warning"
            icon={CircleAlert}
            text={t(
              `link_category_${(link.result?.category ?? "no_applicant") as VerificationDiscoveryCategory}`,
            )}
          />
        )}

        {link.phase === "linked" && (
          <ColocaAlert
            variant="success"
            icon={CheckCircle2}
            text={
              link.result?.mapped_status === "VERIFIED"
                ? t("link_done_verified", { provider: providerLabel })
                : t("link_done")
            }
          />
        )}

        {ownIdentity && (
          <ColocaAlert
            variant="secondary"
            text={t("link_org_identity_hint")}
            actions={
              <ColocaButton
                size="sm"
                variant="default"
                iconTrailing={ArrowUpRight}
                render={<Link to={ACCOUNT_VERIFICATION_ROUTE} />}
              >
                {t("link_org_identity_open")}
              </ColocaButton>
            }
          />
        )}

        {!ownIdentity &&
          link.phase !== "linked" &&
          link.phase !== "found" &&
          link.phase !== "linked_elsewhere" && (
            <form
              className="space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (trimmedId && !manualInvalid && !busy) void link.search(trimmedId);
              }}
            >
              <ColocaLabel htmlFor="cardholder-verification-id">{t("link_manual_label")}</ColocaLabel>
              <div className="flex flex-col gap-2 sm:flex-row">
                <ColocaInput
                  id="cardholder-verification-id"
                  value={manualId}
                  onValueChange={setManualId}
                  placeholder={t("link_manual_placeholder")}
                  aria-invalid={manualInvalid || undefined}
                  autoComplete="off"
                  className="sm:flex-1"
                />
                <ColocaButton
                  type="submit"
                  variant="default"
                  icon={Search}
                  disabled={!trimmedId || manualInvalid || busy}
                  className="w-full sm:w-auto"
                >
                  {t("link_manual_search")}
                </ColocaButton>
              </div>
              <p className={manualInvalid ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
                {manualInvalid ? t("link_manual_invalid") : t("link_manual_hint")}
              </p>
            </form>
          )}
      </div>
    </ColocaDialog>
  );
}
